import { useEffect, useState } from 'react';

// Ratios worth naming when the raw width/height don't reduce to something
// readable (e.g. 1278x720 reduces to 213:120, which tells nobody anything).
const KNOWN_RATIOS = [
  { label: '16:9', value: 16 / 9 },
  { label: '9:16', value: 9 / 16 },
  { label: '4:3', value: 4 / 3 },
  { label: '3:4', value: 3 / 4 },
  { label: '3:2', value: 3 / 2 },
  { label: '2:3', value: 2 / 3 },
  { label: '21:9', value: 21 / 9 },
  { label: '1:1', value: 1 },
];

const greatestCommonDivisor = (a, b) => (b === 0 ? a : greatestCommonDivisor(b, a % b));

// Exact reduction when it lands on small numbers (640x480 → 4:3), otherwise the
// closest named ratio within 2% — beyond that nothing is claimed, since calling
// an odd sensor ratio "16:9" would be worse than showing only the decimal.
export const describeAspectRatio = (width, height) => {
  if (!width || !height) {
    return null;
  }

  const ratio = width / height;
  const divisor = greatestCommonDivisor(Math.round(width), Math.round(height));
  const reducedWidth = Math.round(width) / divisor;
  const reducedHeight = Math.round(height) / divisor;

  if (reducedWidth <= 40 && reducedHeight <= 40) {
    return { ratio, label: `${reducedWidth}:${reducedHeight}` };
  }

  const closest = KNOWN_RATIOS.reduce(
    (best, candidate) => {
      const distance = Math.abs(candidate.value - ratio) / candidate.value;
      return distance < best.distance ? { label: candidate.label, distance } : best;
    },
    { label: null, distance: Infinity }
  );

  return { ratio, label: closest.distance <= 0.02 ? closest.label : null };
};

// Reads the geometry of whatever media element the exercise is currently
// tracking. Every source hangs off the same `videoRef` shape — `{ video,
// stream }` — assigned by hand in WebcamAndSkelatonSide for both the live
// camera branch (fed by CameraManager) and the uploaded-clip branch.
//
// Polled rather than event-driven on purpose: the element behind the ref is
// swapped (camera ↔ upload) without any React state change to hook onto, so
// there's no reliable moment to attach loadedmetadata/resize listeners to the
// *current* element. State is only set when a number actually changes, so a
// steady feed re-renders nothing.
const useMediaAspectRatio = ({ videoRef, enabled = true, intervalMs = 500 }) => {
  const [geometry, setGeometry] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setGeometry(null);
      return undefined;
    }

    const read = () => {
      const element = videoRef?.current?.video ?? null;

      if (!element || !element.videoWidth || !element.videoHeight) {
        setGeometry(current => (current === null ? current : null));
        return;
      }

      const next = {
        width: element.videoWidth,
        height: element.videoHeight,
        // The CSS box the element is painted into — object-fit means this can
        // differ from the source ratio, which is exactly what's worth seeing.
        displayWidth: element.clientWidth,
        displayHeight: element.clientHeight,
      };

      setGeometry(current =>
        current &&
        current.width === next.width &&
        current.height === next.height &&
        current.displayWidth === next.displayWidth &&
        current.displayHeight === next.displayHeight
          ? current
          : next
      );
    };

    read();
    const intervalId = window.setInterval(read, intervalMs);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [videoRef, enabled, intervalMs]);

  if (!geometry) {
    return null;
  }

  return {
    ...geometry,
    source: describeAspectRatio(geometry.width, geometry.height),
    display: describeAspectRatio(geometry.displayWidth, geometry.displayHeight),
  };
};

export default useMediaAspectRatio;
